import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatTabsModule, MatTabChangeEvent } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { ReservationModel } from 'src/app/models/reservation.model';
import { ReservationService } from 'src/app/services/reservation.service';
import { RoomService } from 'src/app/services/room.service';
import { ReservationStatusEnum } from 'src/app/shared/enumerators/reservation-status.enum';
import { DescriptionService } from 'src/app/shared/services/description.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { ReservationDetailDialogComponent } from '../reservation-detail-modal/reservation-detail-modal.component';
import { RoomModel } from 'src/app/models/room.model';
import { CheckInTabComponent } from '../checkin-tab/checkin-tab.component';
import { CheckOutTabComponent } from '../checkout-tab/checkout-tab.component';
import { InProgressTabComponent } from '../inprogress-tab/inprogress-tab.component';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    CheckInTabComponent,
    CheckOutTabComponent,
    InProgressTabComponent
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css'
})
export class DashboardComponent implements OnInit, AfterViewInit {

  dataSourceCheckIn = new MatTableDataSource<ReservationModel>([]);
  dataSourceCheckOut = new MatTableDataSource<ReservationModel>([]);
  dataSourceInProgress = new MatTableDataSource<ReservationModel>([]);

  currentPageCheckIn = 0;
  currentPageCheckOut = 0;
  currentPageInProgress = 0;

  totalCountCheckIn = 0;
  totalCountCheckOut = 0;
  totalCountInProgress = 0;

  pageSizeCheckIn = 10;
  pageSizeCheckOut = 10;
  pageSizeInProgress = 10;

  isLoadingCheckIn = false;
  isLoadingCheckOut = false;
  isLoadingInProgress = false;

  sortField: string = 'checkInDate';
  sortDirection: string = 'asc';
  today: Date = new Date();
  rooms: RoomModel[] = [];

  @ViewChild('paginatorCheckIn') paginatorCheckIn!: MatPaginator;
  @ViewChild('paginatorCheckOut') paginatorCheckOut!: MatPaginator;
  @ViewChild('paginatorInProgress') paginatorInProgress!: MatPaginator;

  @ViewChild('sortCheckIn') sortCheckIn!: MatSort;
  @ViewChild('sortCheckOut') sortCheckOut!: MatSort;
  @ViewChild('sortInProgress') sortInProgress!: MatSort;

  @ViewChild('InProgressTabComponent') inProgressTabComponent!: InProgressTabComponent;

  constructor(
    private reservationSvc: ReservationService,
    private toastr: ToastService,
    private paginatorIntl: MatPaginatorIntl,
    private dialog: MatDialog,
    private roomSrv: RoomService,
    public descriptionService: DescriptionService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.getRooms();
  }

  ngAfterViewInit(): void {
    this.fetchData('checkIn');
  }

  getRooms() {
    this.roomSrv.get().subscribe({
      next: (data) => this.rooms = data,
      error: () => this.toastr.error('Habitaciones', 'Error al obtener habitaciones')
    });
  }

  onTabChange(event: MatTabChangeEvent) {
    const tabTypes = ['checkIn', 'checkOut', 'inProgress'] as const;
    const selected = tabTypes[event.index];
    this.fetchData(selected);
  }

  fetchData(type: 'checkIn' | 'checkOut' | 'inProgress') {
    let skip = 0, top = 10;
    let setLoading = (v: boolean) => { };
    switch (type) {
      case 'checkIn':
        setLoading = v => this.isLoadingCheckIn = v;
        skip = this.currentPageCheckIn * this.pageSizeCheckIn;
        top = this.pageSizeCheckIn;
        break;
      case 'checkOut':
        setLoading = v => this.isLoadingCheckOut = v;
        skip = this.currentPageCheckOut * this.pageSizeCheckOut;
        top = this.pageSizeCheckOut;
        break;
      case 'inProgress':
        setLoading = v => this.isLoadingInProgress = v;
        skip = this.currentPageInProgress * this.pageSizeInProgress;
        top = this.pageSizeInProgress;
        break;
    }

    setLoading(true);
    this.cdr.detectChanges();

    const filters: string[] = [];
    const orderByClause = `$orderby=${this.sortField} ${this.sortDirection}`;
    const todayUtcStr = this.getNowString();

    if (type === 'checkIn')
      filters.push(`checkInDate eq ${todayUtcStr} or ((status eq 'Pending' or status eq 'Reserved') and checkInDate lt ${todayUtcStr})`);
    if (type === 'checkOut')
      filters.push(`checkOutDate eq ${todayUtcStr} or (status eq 'CheckedIn' and checkOutDate lt ${todayUtcStr})`);
    if (type === 'inProgress')
      filters.push(`status eq 'CheckedIn'`);

    const filterClause = filters.length > 0 ? `$filter=${filters.join(' and ')}` : '';
    const paging = `$skip=${skip}&$top=${top}&$count=true`;
    const odataQuery = [filterClause, orderByClause, paging].filter(p => !!p).join('&');

    this.reservationSvc.query(odataQuery).subscribe({
      next: (data: any) => {
        if (type === 'checkIn') {
          this.dataSourceCheckIn.data = data.items;
          this.totalCountCheckIn = data.totalCount ?? data.items.length;
          this.isLoadingCheckIn = false;
        } else if (type === 'checkOut') {
          this.dataSourceCheckOut.data = data.items;
          this.totalCountCheckOut = data.totalCount ?? data.items.length;
          this.isLoadingCheckOut = false;
        } else {
          this.dataSourceInProgress.data = data.items;
          this.totalCountInProgress = data.totalCount ?? data.items.length;
          this.isLoadingInProgress = false;
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.toastr.error('Reservas', 'Error al obtener las reservas');
        setLoading(false);
        this.cdr.detectChanges();
      }
    });
  }

  onAction(event: any, type: 'checkIn' | 'checkOut' | 'inProgress') {
    const { action, reservation } = event;
    switch (action) {
      case 'view': this.viewReservation(reservation); break;
      case 'edit': this.editReservation(reservation); break;
      case 'checkIn': this.checkIn(reservation); break;
      case 'checkOut': this.checkOut(reservation); break;
      case 'cancel': this.cancel(reservation); break;
      case 'payment': this.payment(reservation); break;
    }
  }

  viewReservation(reservation: ReservationModel) {
    reservation.reservationDetails.forEach(rd => rd.room = this.getRoomById(rd.roomId));
    this.dialog.open(ReservationDetailDialogComponent, { data: reservation, width: '700px' });
  }

  getRoomById(roomId: number): RoomModel {
    return this.rooms.find(r => r.id === roomId)!;
  }

  editReservation(reservation: ReservationModel) {
    this.router.navigate([`booking`], { queryParams: { id: reservation.id } });
  }

  checkIn(reservation: ReservationModel) {
    this.router.navigate([`check-in`], { queryParams: { id: reservation.id } });
  }

  checkOut(reservation: ReservationModel) {
    this.updateStatus(reservation, ReservationStatusEnum.CheckedOut, 'Check-out realizado', 'Error en Check-out');
  }

  cancel(reservation: ReservationModel) {
    this.updateStatus(reservation, ReservationStatusEnum.Cancelled, 'Reserva cancelada', 'Error al cancelar');
  }

  payment(reservation: ReservationModel) {
    this.router.navigate([`payment`], { queryParams: { id: reservation.id } });
  }

  updateStatus(reservation: ReservationModel, status: ReservationStatusEnum, messageOk: string, messageError: string) {
    this.reservationSvc.updateStatus(reservation.id, status).subscribe({
      next: (ok) => ok ? this.toastr.success('Reserva', messageOk) : this.toastr.error('Reserva', messageError),
      error: () => this.toastr.error('Reserva', messageError)
    });
  }

  getNowString(formated: boolean = false) {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return formated ? `${d}-${m}-${y}` : `${y}-${m}-${d}`;
  }

  getErrorCheckInClass(row: ReservationModel): string {
    const checkIn = new Date(row.checkInDate);
    const today = new Date();
    checkIn.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return (
      [ReservationStatusEnum.Pending, ReservationStatusEnum.Reserved].includes(row.status) &&
      checkIn < today
    ) ? 'row-error' : '';
  }

  getErrorCheckOutClass(row: ReservationModel): string {
    const checkOut = new Date(row.checkOutDate);
    const today = new Date();
    checkOut.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return (
      row.status === ReservationStatusEnum.CheckedIn && checkOut < today
    ) ? 'row-error' : '';
  }

  onPageChange(event: any, type: 'checkIn' | 'checkOut' | 'inProgress') {
    const pageIndex = event.pageIndex;
    const pageSize = event.pageSize;

    switch (type) {
      case 'checkIn':
        this.currentPageCheckIn = pageIndex;
        this.pageSizeCheckIn = pageSize;
        this.fetchData('checkIn');
        break;
      case 'checkOut':
        this.currentPageCheckOut = pageIndex;
        this.pageSizeCheckOut = pageSize;
        this.fetchData('checkOut');
        break;
      case 'inProgress':
        this.currentPageInProgress = pageIndex;
        this.pageSizeInProgress = pageSize;
        this.fetchData('inProgress');
        break;
    }
  }

  canCheckIn(reservation: ReservationModel): boolean {
    const today = new Date();
    const checkIn = new Date(reservation.checkInDate ?? '');
    return reservation.status === ReservationStatusEnum.Reserved &&
      checkIn.toDateString() === today.toDateString();
  }

  canCheckOut(reservation: ReservationModel): boolean {
    return reservation.status === ReservationStatusEnum.CheckedIn;
  }

  canCancel(reservation: ReservationModel): boolean {
    return reservation.status === ReservationStatusEnum.Reserved || reservation.status === ReservationStatusEnum.Pending;
  }

  canEdit(reservation: ReservationModel): boolean {
    return reservation.status === ReservationStatusEnum.Reserved
      || reservation.status === ReservationStatusEnum.Pending
      || reservation.status === ReservationStatusEnum.CheckedIn;
  }

  canRegisterPayment(reservation: ReservationModel): boolean {
    let totalAmount = reservation?.payments?.reduce((sum, p) => sum + (p.amount ?? 0), 0) || 0;
    return reservation.totalPrice > totalAmount
      && (reservation.status === ReservationStatusEnum.Pending
        || reservation.status === ReservationStatusEnum.Reserved);
  }

}
